import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { createContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import HomePage from "@/pages/HomePage";
import YcDemoPage from "@/pages/YcDemoPage";
import AcuteDemoPage from "@/pages/AcuteDemoPage";
import ValidatorBankPage from "@/pages/ValidatorBankPage";
import DoctorValidatorBankPage from "@/pages/DoctorValidatorBankPage";
import ClinicalCdsPage from "@/pages/ClinicalCdsPage";
import AccessibilityPage from "@/pages/AccessibilityPage";
import AdminFeedbackPage from "@/pages/AdminFeedbackPage";
import AdminAnticholinergicPage from "@/pages/AdminAnticholinergicPage";
import PhysicianReviewPage from "@/pages/PhysicianReviewPage";
import AdminTrustMetricsPage from "@/pages/AdminTrustMetricsPage";
import AdminAuditTrustPage from "@/pages/AdminAuditTrustPage";
import QuickFeedbackPage from "@/pages/QuickFeedbackPage";
import VaultPage from "@/pages/VaultPage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import ClinicianRoundsPage from "@/pages/ClinicianRoundsPage";
import VerifyPage from "@/pages/VerifyPage";
import SystemHealthPage from "@/pages/SystemHealthPage";
import AttestationLedgerPage from "@/pages/AttestationLedgerPage";
import InvestorAccessPage from "@/pages/InvestorAccessPage";
import PhysicianScribePage from "@/pages/PhysicianScribePage";
import AccessibilityToolbar from "@/components/AccessibilityToolbar";
import "@/styles/accessibility-modes.css";
import { startBrowseTracking } from "@/lib/browseTracker";
import { Toaster } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

// Founder Mode Context — bypasses rate limits when PIN "sivam" entered
export const FounderContext = createContext({ isFounder: false });

// Demo Mode Context — when ON, hides latency-sensitive features (Agent Chat) for clean YC demos
export const DemoModeContext = createContext({ demoMode: false, setDemoMode: () => {} });

// Item 3 — Clinical Validator Context — activated when URL path is /register/validator
// No tokens, no whitelist. Validator fills name/email, server auto-generates token.
export const ValidatorContext = createContext({ isValidator: false });

function App() {
  const [isFounder, setIsFounder] = useState(false);
  const [showRateModal, setShowRateModal] = useState(false);
  const [demoMode, setDemoModeState] = useState(false);
  // Item 3 — validator mode detected purely from URL path (no token round-trip)
  const isValidatorPath = typeof window !== 'undefined' && window.location.pathname.startsWith('/register/validator');
  const [isValidator] = useState(isValidatorPath);

  // Check sessionStorage on load
  useEffect(() => {
    if (sessionStorage.getItem('pai_founder_mode') === 'active') {
      setIsFounder(true);
    }
    // URL override: ?demo=1 forces Demo Mode on (founder Loom-recording convenience, May 26 2026)
    const urlDemo = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('demo') === '1';
    if (urlDemo) {
      setDemoModeState(true);
      return;
    }
    // Fetch current demo mode from backend
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings`)
      .then(res => setDemoModeState(!!res.data?.demoMode))
      .catch(() => {});
  }, []);

  // Anonymous browse-session tracking (public pages only; skips founder mode) — 2026-07-04
  useEffect(() => { startBrowseTracking(); }, []);

  // Global 429 interceptor
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 429 && !isFounder) {
          setShowRateModal(true);
          return Promise.reject(error);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [isFounder]);

  const activateFounder = useCallback((pin) => {
    const p = (pin || '').trim();
    // Low-stakes demo/iteration tool — accept the dedicated founder PIN OR the
    // founder master passcode (rasi%puram) so there is one code to remember.
    if (p.toLowerCase() === 'pai-fndr-50u3co2h' || p === 'rasi%puram') {
      setIsFounder(true);
      sessionStorage.setItem('pai_founder_mode', 'active');
      return true;
    }
    return false;
  }, []);

  return (
    <FounderContext.Provider value={{ isFounder, activateFounder }}>
      <DemoModeContext.Provider value={{ demoMode, setDemoMode: setDemoModeState }}>
        <ValidatorContext.Provider value={{ isValidator }}>
        <div className="App">
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/register/validator" element={<HomePage />} />
              <Route path="/yc-demo" element={<YcDemoPage />} />
              <Route path="/acute-demo" element={<AcuteDemoPage />} />
              <Route path="/validator-bank" element={<ValidatorBankPage />} />
              <Route path="/validator-bank/:slug" element={<DoctorValidatorBankPage />} />
              <Route path="/clinical-cds/:slug" element={<ClinicalCdsPage />} />
              <Route path="/accessibility" element={<AccessibilityPage />} />
              <Route path="/admin/feedback" element={<AdminFeedbackPage />} />
              <Route path="/admin/anticholinergic" element={<AdminAnticholinergicPage />} />
              <Route path="/physician-review/:encounterId" element={<PhysicianReviewPage />} />
              <Route path="/admin/trust-metrics" element={<AdminTrustMetricsPage />} />
              <Route path="/admin/audit-trust" element={<AdminAuditTrustPage />} />
              <Route path="/feedback" element={<QuickFeedbackPage />} />
              <Route path="/vault" element={<VaultPage />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/rounds" element={<ClinicianRoundsPage />} />
              <Route path="/verify" element={<VerifyPage />} />
              <Route path="/health" element={<SystemHealthPage />} />
              <Route path="/attestation-ledger" element={<AttestationLedgerPage />} />
              <Route path="/investor-access" element={<InvestorAccessPage />} />
              <Route path="/dictate-scribe" element={<PhysicianScribePage />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
          <AccessibilityToolbar />

          {/* Item 3 — Validator banner rendered INLINE next to "Register Interest" button (see EarlyAccess.js) */}

          {/* Rate Limit Modal */}
          <Dialog open={showRateModal} onOpenChange={setShowRateModal}>
            <DialogContent className="max-w-md text-center p-8" data-testid="rate-limit-modal">
              <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-black mb-2">Session Limit Reached</h3>
              <p className="text-sm text-slate-600 mb-4">
                You are limited to current use in this Demo environment. In Full Production, this feature is unlimited.
              </p>
              <p className="text-sm text-slate-500 mb-4">
                Please contact Admin for extended access.
              </p>
              <Button onClick={() => setShowRateModal(false)} className="bg-[#2563EB] hover:bg-blue-700 rounded-md">
                Understood
              </Button>
            </DialogContent>
          </Dialog>
        </div>
        </ValidatorContext.Provider>
      </DemoModeContext.Provider>
    </FounderContext.Provider>
  );
}

export default App;
